import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { CustomButton } from '@/components/molecules/CustomButton';
import { PokemonPicker } from '@/components/molecules/PokemonPicker';
import { Loader } from '@/components/atoms/Loader';
import {
  getTrade,
  selectTradePokemon,
  confirmTrade,
  cancelTrade,
  type Trade as TradeData,
  type TradePokemon,
} from '@/services/trade';
import { tryGetMeUser } from '@/services/user';
import { useCountdown } from '@/hooks/useCountdown';
import { cdnUrl } from '@/utils/cdn';

const TERMINAL_STATUSES = ['completed', 'cancelled', 'expired'] as const;

interface PokemonCardProps {
  pokemon: TradePokemon;
  username: string;
  confirmed?: boolean;
}
const PokemonCard = ({ pokemon, username, confirmed }: PokemonCardProps) => (
  <div className="flex flex-1 flex-col items-center gap-2 rounded-2xl bg-surface p-4 shadow-sm">
    <p className="text-content/50 text-xs">{username} propose</p>
    <div className="bg-primary-500/10 flex h-20 w-20 items-center justify-center rounded-2xl">
      <img
        src={cdnUrl(pokemon.sprite)}
        alt={pokemon.nickname}
        className="h-16 w-16 object-contain"
        onError={(e) => { e.currentTarget.style.opacity = '0'; }}
      />
    </div>
    <p className="text-content font-lexend font-semibold">
      {pokemon.nickname}
      {pokemon.isShiny && (
        <span className="text-primary-500 ml-1 text-xs">★</span>
      )}
    </p>
    <p className="text-content/50 text-xs">{pokemon.realname} · Nv.&nbsp;{pokemon.level}</p>
    {confirmed !== undefined && (
      <span
        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
          confirmed
            ? 'bg-green-500/15 text-green-600'
            : 'bg-yellow-500/15 text-yellow-600'
        }`}
      >
        {confirmed ? '✅ Confirmé' : '⏳ En attente'}
      </span>
    )}
  </div>
);

const CenteredLayout = ({ children }: { children: React.ReactNode }) => (
  <main className="bg-page flex min-h-screen items-center justify-center p-5">
    <div className="w-full max-w-md text-center">{children}</div>
  </main>
);

export const Trade = () => {
  const { tradeId } = useParams<{ tradeId: string }>();
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<string | null>(null);

  const {
    data: trade,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['trade', tradeId],
    queryFn: () => getTrade(tradeId!),
    enabled: !!tradeId,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      if ((TERMINAL_STATUSES as readonly string[]).includes(status)) return false;
      return 3000;
    },
  });

  const { data: meUser } = useQuery({
    queryKey: ['meSilent'],
    queryFn: tryGetMeUser,
    staleTime: 60_000,
    retry: false,
  });

  const myRole = useMemo(() => {
    if (!meUser || !trade) return 'spectator' as const;
    if (meUser.userId === trade.initiator.userId) return 'initiator' as const;
    if (meUser.userId === trade.recipient.userId) return 'recipient' as const;
    return 'spectator' as const;
  }, [meUser, trade]);

  const countdown = useCountdown(trade?.expiresAt ?? new Date(0).toISOString());

  const updateCache = (updated: TradeData) =>
    queryClient.setQueryData(['trade', tradeId], updated);

  const { mutate: selectPokemon, isPending: isSelecting } = useMutation<
    TradeData,
    Error,
    string
  >({
    mutationFn: (ownedPokemonId) => selectTradePokemon(tradeId!, ownedPokemonId),
    onSuccess: updateCache,
    onError: (err) => setMutationError(err.message),
  });

  const { mutate: confirm, isPending: isConfirming } = useMutation<
    TradeData,
    Error,
    void
  >({
    mutationFn: () => confirmTrade(tradeId!),
    onSuccess: updateCache,
    onError: (err) => setMutationError(err.message),
  });

  const { mutate: cancel, isPending: isCancelling } = useMutation<
    TradeData,
    Error,
    void
  >({
    mutationFn: () => cancelTrade(tradeId!),
    onSuccess: updateCache,
    onError: (err) => setMutationError(err.message),
  });

  if (isLoading) {
    return (
      <main className="bg-page flex min-h-screen items-center justify-center">
        <Loader size={48} color="#f29602" />
      </main>
    );
  }

  if (isError || !trade) {
    return (
      <CenteredLayout>
        <p className="mb-4 text-5xl">❓</p>
        <h2 className="text-content font-lexend mb-2 text-xl font-semibold">
          Trade introuvable
        </h2>
        <p className="text-content/60 text-sm">
          Ce lien d'échange est invalide ou a déjà été supprimé.
        </p>
      </CenteredLayout>
    );
  }

  if (trade.status === 'expired' || countdown.isExpired) {
    return (
      <CenteredLayout>
        <p className="mb-4 text-5xl">⏳</p>
        <h2 className="text-content font-lexend mb-2 text-xl font-semibold">
          Échange expiré
        </h2>
        <p className="text-content/60 text-sm">
          Ce trade a expiré. Retourne sur Discord pour en initier un nouveau.
        </p>
      </CenteredLayout>
    );
  }

  if (trade.status === 'cancelled') {
    return (
      <CenteredLayout>
        <p className="mb-4 text-5xl">❌</p>
        <h2 className="text-content font-lexend mb-2 text-xl font-semibold">
          Échange annulé
        </h2>
        <p className="text-content/60 mb-6 text-sm">
          Cet échange a été annulé.
        </p>
        <Link to="/pokemons">
          <CustomButton title="Voir mon PC" color="primary" rounded="full" />
        </Link>
      </CenteredLayout>
    );
  }

  if (trade.status === 'completed') {
    const initiatorReceives = trade.recipient.pokemon;
    const recipientReceives = trade.initiator.pokemon;
    return (
      <CenteredLayout>
        <p className="mb-4 text-5xl">🎉</p>
        <h2 className="text-content font-lexend mb-3 text-xl font-semibold">
          Échange conclu&nbsp;!
        </h2>
        {initiatorReceives && recipientReceives && (
          <div className="bg-surface mb-6 rounded-2xl p-4 text-sm">
            <p className="text-content">
              <span className="font-semibold">{trade.initiator.username}</span> reçoit{' '}
              <span className="font-semibold">{initiatorReceives.nickname}</span>
            </p>
            <p className="text-content/40 my-1 text-lg">⇅</p>
            <p className="text-content">
              <span className="font-semibold">{trade.recipient.username}</span> reçoit{' '}
              <span className="font-semibold">{recipientReceives.nickname}</span>
            </p>
          </div>
        )}
        <Link to="/pokemons">
          <CustomButton title="Voir mon PC" color="primary" rounded="full" />
        </Link>
      </CenteredLayout>
    );
  }

  const myTurn =
    (trade.status === 'pending_a_pick' && myRole === 'initiator') ||
    (trade.status === 'pending_b_pick' && myRole === 'recipient');

  const myConfirmed =
    myRole === 'initiator'
      ? trade.initiatorConfirmed
      : myRole === 'recipient'
        ? trade.recipientConfirmed
        : false;

  const otherParticipant =
    myRole === 'initiator' ? trade.recipient : trade.initiator;

  const myParticipant =
    myRole === 'initiator' ? trade.initiator : trade.recipient;

  const header = (
    <div className="flex items-center justify-between rounded-2xl bg-surface px-5 py-4 shadow-sm">
      <div>
        <p className="text-content/50 text-xs">Échange</p>
        <p className="text-content font-lexend font-semibold">
          {trade.initiator.username}&nbsp;↔&nbsp;{trade.recipient.username}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Clock
          size={14}
          className={
            countdown.remaining < 120 ? 'text-red-500' : 'text-content/40'
          }
        />
        <span
          className={`font-lexend text-sm font-semibold tabular-nums ${
            countdown.remaining < 120 ? 'text-red-500' : 'text-content/60'
          }`}
        >
          {countdown.formatted}
        </span>
      </div>
    </div>
  );

  if (trade.status === 'pending_confirm') {
    return (
      <main className="bg-page min-h-screen">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-5">
          {header}
          <GenericCard title="Confirmer l'échange&nbsp;?">
            <div className="mb-5 flex gap-3">
              {trade.initiator.pokemon && (
                <PokemonCard
                  pokemon={trade.initiator.pokemon}
                  username={trade.initiator.username}
                  confirmed={trade.initiatorConfirmed}
                />
              )}
              <div className="flex items-center self-center text-2xl">⇄</div>
              {trade.recipient.pokemon && (
                <PokemonCard
                  pokemon={trade.recipient.pokemon}
                  username={trade.recipient.username}
                  confirmed={trade.recipientConfirmed}
                />
              )}
            </div>

            {mutationError && (
              <div className="mb-4 rounded-xl bg-red-500/10 px-4 py-3">
                <p className="text-sm font-medium text-red-600">{mutationError}</p>
              </div>
            )}

            {myRole !== 'spectator' && (
              <div className="flex gap-3">
                {!myConfirmed ? (
                  <>
                    <CustomButton
                      title="Confirmer"
                      color="primary"
                      rounded="full"
                      fullWidth
                      loading={isConfirming}
                      onClick={() => {
                        setMutationError(null);
                        confirm();
                      }}
                    />
                    <CustomButton
                      title="Annuler"
                      color="secondary"
                      variant="ghost"
                      rounded="full"
                      loading={isCancelling}
                      onClick={() => {
                        setMutationError(null);
                        cancel();
                      }}
                    />
                  </>
                ) : (
                  <div className="flex w-full flex-col items-center gap-2">
                    <p className="text-content/60 text-sm">
                      En attente de la confirmation de&nbsp;
                      <span className="font-semibold">
                        {otherParticipant.username}
                      </span>
                      …
                    </p>
                    <CustomButton
                      title="Annuler"
                      color="secondary"
                      variant="ghost"
                      size="sm"
                      rounded="full"
                      loading={isCancelling}
                      onClick={() => {
                        setMutationError(null);
                        cancel();
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {myRole === 'spectator' && (
              <p className="text-content/50 text-center text-sm">
                Échange en cours de confirmation…
              </p>
            )}
          </GenericCard>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-page min-h-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-5">
        {header}

        {otherParticipant.pokemon && (
          <div className="flex items-center gap-3 rounded-2xl bg-surface px-5 py-4 shadow-sm">
            <div className="bg-primary-500/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
              <img
                src={cdnUrl(otherParticipant.pokemon.sprite)}
                alt={otherParticipant.pokemon.nickname}
                className="h-10 w-10 object-contain"
                onError={(e) => { e.currentTarget.style.opacity = '0'; }}
              />
            </div>
            <div>
              <p className="text-content/50 text-xs">
                {otherParticipant.username} propose
              </p>
              <p className="text-content font-lexend text-sm font-semibold">
                {otherParticipant.pokemon.nickname}
                {otherParticipant.pokemon.isShiny && (
                  <span className="text-primary-500 ml-1">★</span>
                )}
              </p>
              <p className="text-content/50 text-xs">
                {otherParticipant.pokemon.realname} · Nv.&nbsp;
                {otherParticipant.pokemon.level}
              </p>
            </div>
          </div>
        )}

        <GenericCard
          title={
            myTurn
              ? 'Choisir ton Pokémon'
              : myRole !== 'spectator' && myParticipant.pokemon
                ? 'Tu as proposé'
                : "En attente…"
          }
        >
          {myTurn && (
            <>
              <PokemonPicker
                onSelect={(ownedPokemon) => {
                  setMutationError(null);
                  selectPokemon(ownedPokemon.ownedPokemonId);
                }}
                loading={isSelecting}
                queryKey={`trade-picker-${tradeId}`}
              />
              {mutationError && (
                <div className="mt-4 rounded-xl bg-red-500/10 px-4 py-3">
                  <p className="text-sm font-medium text-red-600">
                    {mutationError}
                  </p>
                </div>
              )}
            </>
          )}

          {!myTurn && myRole !== 'spectator' && myParticipant.pokemon && (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="bg-primary-500/10 flex h-20 w-20 items-center justify-center rounded-2xl">
                <img
                  src={cdnUrl(myParticipant.pokemon.sprite)}
                  alt={myParticipant.pokemon.nickname}
                  className="h-16 w-16 object-contain"
                  onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                />
              </div>
              <p className="text-content font-lexend font-semibold">
                {myParticipant.pokemon.nickname}
                {myParticipant.pokemon.isShiny && (
                  <span className="text-primary-500 ml-1">★</span>
                )}
              </p>
              <p className="text-content/50 text-sm">
                En attente du choix de&nbsp;
                <span className="font-semibold">
                  {otherParticipant.username}
                </span>
                …
              </p>
            </div>
          )}

          {!myTurn && (myRole === 'spectator' || !myParticipant.pokemon) && (
            <div className="flex flex-col items-center gap-2 py-8">
              <Loader size={32} color="#f29602" />
              <p className="text-content/50 mt-2 text-sm">
                {myRole === 'spectator'
                  ? `En attente du choix de ${
                      trade.status === 'pending_a_pick'
                        ? trade.initiator.username
                        : trade.recipient.username
                    }…`
                  : `En attente du choix de ${otherParticipant.username}…`}
              </p>
            </div>
          )}
        </GenericCard>
      </div>
    </main>
  );
};